import { SelectChangeEvent } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { dispatch } from 'store';
import { SiteUuidKey, setSiteUuid } from 'store/Auth/Links/BaseLinksSlice';
import useBaseLinks from 'store/Auth/Links/useBaseLinks';
import useAuth from 'store/Auth/useAuth';
import { useGetSitesTableDataQuery } from 'store/_Api/Sites';
import pinPoint from 'atoms/icons/svg/pin-point.svg';
import Dropdown from 'atoms/components/Dropdown';
import { DropDownProps } from 'atoms/components/Dropdown/Dropdown';

type Props = {
  clickableNodeOverride?: DropDownProps['clickableNodeOverride'];
};

const SiteDropdown: FC<Props> = (props) => {
  const { getLink, selectedSiteUuid } = useBaseLinks();
  const siteLinkForCustomers = getLink('mini-sites');
  const { isCustomer, profile } = useAuth();
  const [sitesLink, setSitesLink] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (siteLinkForCustomers && isCustomer && profile?.customer_name) {
      setSitesLink(siteLinkForCustomers);
      return;
    }
  }, [siteLinkForCustomers, isCustomer, profile?.customer_name]);

  const { data: sites, isLoading } = useGetSitesTableDataQuery(
    sitesLink ?? skipToken,
  );

  const options = useMemo(() => {
    if (sites && sites.collection) {
      return sites.collection.map(({ site_uuid, name }) => ({
        value: site_uuid,
        label: name,
        icon: <img src={pinPoint} alt="pin point" />,
      }));
    }

    return [];
  }, [sites]);

  const handleSiteItemClick = (id: string) => {
    dispatch(setSiteUuid(id));
    navigate('/dashboard');
  };

  // storing the site into query params
  useEffect(() => {
    if (selectedSiteUuid) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(SiteUuidKey, selectedSiteUuid);
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [selectedSiteUuid]);

  // setting it to default first
  useEffect(() => {
    if (sites?.collection && sites.collection.length > 0) {
      if (!selectedSiteUuid) {
        dispatch(setSiteUuid(sites.collection[0].site_uuid));
      }
    }
  }, [sites?.collection, selectedSiteUuid]);

  return (
    <Dropdown
      label={t('Location')}
      value={selectedSiteUuid}
      options={options}
      isLoading={isLoading}
      onChange={(e: SelectChangeEvent<unknown>) =>
        handleSiteItemClick(e.target.value as string)
      }
      {...props}
    />
  );
};

export default SiteDropdown;
